import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { formatCurrency, formatPercentage } from '@app/shared/utils';
import { TableConfig } from './linked-ownership';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'common-ownership-table-view',
  templateUrl: './common-ownership-table-view.component.html',
  styleUrls: ['./common-ownership-table-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommonOwnershipTableViewComponent implements OnChanges {
  @Input() title: string;
  @Input() records: any[] = [];
  @Input() categoryKey: string;
  @Input() canCreateNew = false;
  @Input() showSelection = false;
  @Input() showActions = false;
  @Input() rowActions: string[] = [];
  @Input() hasAutoAddSetting = false;
  @Input() isBuySellTemplate = false;
  @Input() linkedItems: any[] = [];
  @Input() searchBoxPlaceHolder = '';
  @Input() tableConfig!: TableConfig;
  @Input() allUserInstanceForms: any[] = [];
  @Input() userSettingPreference = false;
  @Input() isRemoveEditSaveSharing = false;
  @Output() onCellDataClickEventEmitter = new EventEmitter<any>();
  @Output() onItemSelectionChangeEventEmitter = new EventEmitter<any>();
  @Output() onSelectionChange = new EventEmitter<{ selectedRows: number[]; selectedItems: any[] }>();
  @Output() onRemoveSelected = new EventEmitter<any[]>();
  @Output() onToggleChangeEventEmitter = new EventEmitter<boolean>();
  @Output() onRowActionClick = new EventEmitter<{ action: string; rowIndex: number; record: any }>();
  @Output() onCheckboxSelectionChange = new EventEmitter<{
    record: any;
    index: number;
    isChecked: boolean;
    categoryKey: string;
  }>();

  private readonly actionIcons: { [key: string]: string } = {
    delete: 'delete_outline',
  };

  private readonly actionIconClasses: { [key: string]: string } = {
    delete: 'text-[#EB5757]',
  };

  private readonly actionMenuClasses: { [key: string]: string } = {
    delete: 'text-[#EB5757]',
  };

  private readonly actionLabels: { [key: string]: string } = {
    delete: 'Remove',
  };

  selectAll = false;
  processedRecords = [];
  selectedItemsArray: Array<any> = [];
  selectedRows: Set<number> = new Set();
  summaryData = { hasSummary: false, summaryLabel: '', summaryValues: {} as Record<string, string> };

  constructor(private cd: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.tableConfig?.columns?.length) {
      this.processedRecords = [];
      this.summaryData = { hasSummary: false, summaryLabel: '', summaryValues: {} };
      return;
    }
    if (changes['tableConfig'] || changes['records']) {
      this.processRecords();
      this.selectedRows.clear();
      this.selectedItemsArray = [];
      this.selectAll = false;
    }
    if (changes['userSettingPreference']) {
      this.userSettingPreference = changes['userSettingPreference'].currentValue;
    }
  }

  private processRecords(): void {
    const config = this.tableConfig;
    this.processedRecords = (this.records || []).map((rec, idx) => {
      const cells: Record<string, any> = {};
      for (const col of config.columns) {
        const property = config.propertyMap[col.column_id] ?? col.column_id;
        const raw = rec?.[property];
        if (col.column_type === 'text') {
          cells[col.column_id] = raw ?? '';
        } else if (col.column_type === 'percentage') {
          cells[col.column_id] = raw ? formatPercentage(Number(raw), true) : '';
        } else if (col.column_type === 'currency') {
          const isBeneficialOwnershipEntity = this.categoryKey === 'specific_gifts' && rec?.is_asset === 0 && rec?.is_liability === 0;
          if (isBeneficialOwnershipEntity) {
            cells[col.column_id] = 'Not Applicable';
          } else {
            const value = Number(raw || 0);
            cells[col.column_id] = raw !== null && raw !== undefined ? formatCurrency(value) : '';
          }
        }
      }
      return { index: idx, record: rec, cells, selected: false };
    });

    if (this.tableConfig.summary?.enabled && this.tableConfig.summary.currencyColumnId) {
      const currencyColumnId = this.tableConfig.summary.currencyColumnId;
      const property = this.tableConfig.propertyMap[currencyColumnId] ?? currencyColumnId;
      const netValue = (this.records || []).reduce((a, r) => a + (Number(r?.[property]) || 0), 0);
      this.summaryData = {
        hasSummary: true,
        summaryLabel: this.tableConfig.summary.label,
        summaryValues: { [currencyColumnId]: formatCurrency(netValue) },
      };
    } else {
      this.summaryData = { hasSummary: false, summaryLabel: '', summaryValues: {} };
    }
  }

  get formattedRowActions() {
    return this.rowActions.map((action) => ({
      action,
      icon: this.actionIcons[action] || 'more_vert',
      iconClass: this.actionIconClasses[action] || 'text-[#6B7280]',
      menuClass: this.actionMenuClasses[action] || 'text-[#6B7280]',
      label: this.actionLabels[action] || action,
    }));
  }

  onRowSelectionChange(rowIndex: number, isSelected: boolean) {
    const row = this.processedRecords.find((r) => r.index === rowIndex);
    if (row) {
      row.selected = isSelected;
    }

    if (isSelected) {
      this.selectedRows.add(rowIndex);
    } else {
      this.selectedRows.delete(rowIndex);
    }

    this.selectedItemsArray = this.processedRecords
      .filter((row) => row.selected)
      .map((row) => ({
        rowIndex: row.index,
        data: row,
      }));

    this.updateSelectAllState();
    this.cd.detectChanges();
    this.onSelectionChange.emit({
      selectedRows: Array.from(this.selectedRows),
      selectedItems: this.selectedItemsArray,
    });

    if (row) {
      this.onCheckboxSelectionChange.emit({
        record: row.record,
        index: rowIndex,
        isChecked: isSelected,
        categoryKey: this.categoryKey,
      });
    }
  }

  onSelectAllChange(selected: boolean) {
    this.selectAll = selected;
    this.processedRecords.forEach((row) => {
      row.selected = selected;
    });
    if (selected) {
      this.processedRecords.forEach((_, index) => {
        this.selectedRows.add(index);
      });
    } else {
      this.selectedRows.clear();
    }
    this.selectedItemsArray = this.processedRecords
      .filter((row) => row.selected)
      .map((row) => ({
        rowIndex: row.index,
        data: row,
      }));

    this.cd.detectChanges();
    this.onSelectionChange.emit({
      selectedRows: Array.from(this.selectedRows),
      selectedItems: this.selectedItemsArray,
    });

    this.processedRecords.forEach((row, index) => {
      this.onCheckboxSelectionChange.emit({
        record: row.record,
        index: index,
        isChecked: selected,
        categoryKey: this.categoryKey,
      });
    });
  }

  updateSelectAllState(): void {
    this.selectAll = this.processedRecords.every((row) => row.selected) && this.processedRecords.length > 0;
  }

  onRowActionHandler(processedRow, actionData, event?: Event) {
    this.onRowActionClick.emit({ action: actionData.action, rowIndex: processedRow.index, record: processedRow.record });
  }

  floaterActionHandler(event: string) {
    if (event === 'close') {
      this.processedRecords.forEach((row) => {
        row.selected = false;
      });
      this.selectedRows.clear();
      this.selectedItemsArray = [];
      this.selectAll = false;
      this.cd.detectChanges();
      this.onSelectionChange.emit({
        selectedRows: [],
        selectedItems: [],
      });
    } else if (event === 'removeSuggestion') {
      this.onRemoveSelected.emit(this.selectedItemsArray.map((item) => item.data.record));
    }
  }

  onItemSelectionChange(event: any) {
    this.onItemSelectionChangeEventEmitter.emit(event);
  }

  onToggleChange(isChecked: boolean) {
    this.userSettingPreference = isChecked;
    this.onToggleChangeEventEmitter.emit(isChecked);
  }

  trackRowBy = (_: number, r: { index: number }) => r.index;

  trackColumnBy = (_: number, c: { column_id: string }) => c.column_id;

  onCellDataClick(record: any) {
    this.onCellDataClickEventEmitter.emit({ action: 'openTemplate', record });
  }

  clearSelections() {
    this.processedRecords.forEach((row) => {
      row.selected = false;
    });
    this.selectedRows.clear();
    this.selectedItemsArray = [];
    this.selectAll = false;
    this.cd.detectChanges();
    this.onSelectionChange.emit({
      selectedRows: [],
      selectedItems: [],
    });
  }
}
